import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi, type JobLog, type SystemSetting } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type { ColumnDef } from "@/components/ui";
import { Download, Save, RefreshCw, Mail, Clock, CheckCircle2, XCircle, AlertCircle, Info, Send, Bell, CreditCard, FileText, Users, CalendarClock } from "lucide-react";
import { downloadCsv } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

interface EmailCronTab {
  id: "logs" | "settings" | "info";
  label: string;
  icon: React.ElementType;
}

const TABS: EmailCronTab[] = [
  { id: "logs", label: "Jobblogg", icon: Clock },
  { id: "settings", label: "Inställningar", icon: Mail },
  { id: "info", label: "Info", icon: Info },
];

const STATUS_OPTIONS = [
  { value: "", label: "Alla statusar" },
  { value: "completed", label: "Slutförd" },
  { value: "failed", label: "Misslyckad" },
  { value: "started", label: "Startad" },
];

const JOB_TYPE_OPTIONS = [
  { value: "", label: "Alla jobbtyper" },
  { value: "email:", label: "E-post" },
  { value: "cron:", label: "Cron-jobb" },
  { value: "notification:", label: "Notifikationer" },
];

// ═══════════════════════════════════════════════════════════════════
// Helper functions
// ═══════════════════════════════════════════════════════════════════

function getStatusBadge(status: string) {
  switch (status) {
    case "completed":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle2 size={12} />
          Slutförd
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle size={12} />
          Misslyckad
        </span>
      );
    case "started":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <AlertCircle size={12} />
          Startad
        </span>
      );
    default:
      return (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          {status}
        </span>
      );
  }
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatJobType(jobType: string): string {
  // Format "email:welcome" -> "E-post: welcome"
  // Format "cron:check_deadlines" -> "Cron: check_deadlines"
  if (jobType.startsWith("email:")) {
    return `E-post: ${jobType.substring(6)}`;
  }
  if (jobType.startsWith("cron:")) {
    return `Cron: ${jobType.substring(5)}`;
  }
  if (jobType.startsWith("notification:")) {
    return `Notis: ${jobType.substring(13)}`;
  }
  return jobType;
}

// ═══════════════════════════════════════════════════════════════════
// Job Logs Tab
// ═══════════════════════════════════════════════════════════════════

function JobLogsTab() {
  const {
    data,
    isLoading,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
    refresh,
  } = useDataTable<JobLog, { status: string; jobType: string; createdAt: string }>({
    fetcher: (params) =>
      adminApi.listJobLogs({
        page: params.page,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        status: params.status || undefined,
        jobType: params.jobType || undefined,
        createdAfter: params.createdAt || undefined,
      }),
    pageSize: 25,
    initialFilters: { status: "", jobType: "", createdAt: "" },
    sortKeyMap: {
      jobType: "jobType",
      status: "status",
      createdAt: "createdAt",
      durationMs: "durationMs",
    },
  });

  const columns: ColumnDef<JobLog>[] = useMemo(
    () => [
      {
        id: "jobType",
        header: "Jobbtyp",
        sortable: true,
        sortAccessor: (row) => row.jobType,
        filter: {
          type: "select" as const,
          value: filters.jobType,
          onChange: (v: string) => setFilter("jobType", v),
          options: JOB_TYPE_OPTIONS,
        },
        cell: (row) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">
              {formatJobType(row.jobType)}
            </span>
            {row.jobId && (
              <span className="text-xs text-gray-400">
                ID: {row.jobId.slice(0, 8)}...
              </span>
            )}
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        sortAccessor: (row) => row.status,
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: STATUS_OPTIONS,
        },
        cell: (row) => getStatusBadge(row.status),
      },
      {
        id: "durationMs",
        header: "Tid",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => row.durationMs ?? 0,
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {formatDuration(row.durationMs)}
          </span>
        ),
      },
      {
        id: "createdAt",
        header: "Tidpunkt",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: filters.createdAt,
          onChange: (v: string) => setFilter("createdAt", v),
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleString("sv-SE")}
          </span>
        ),
      },
    ],
    [filters.jobType, filters.status, filters.createdAt, setFilter]
  );

  const expandedContent = (log: JobLog) => {
    return (
      <div className="bg-gray-50 rounded-lg p-3 space-y-3">
        {log.errorMessage && (
          <div>
            <p className="text-xs font-medium text-red-600 mb-1">Felmeddelande:</p>
            <pre className="text-xs text-red-700 bg-red-50 p-2 rounded overflow-x-auto whitespace-pre-wrap">
              {log.errorMessage}
            </pre>
          </div>
        )}
        {log.inputData !== null && log.inputData !== undefined && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Indata:</p>
            <pre className="text-xs text-gray-700 bg-white p-2 rounded border overflow-x-auto">
              {JSON.stringify(log.inputData as object, null, 2)}
            </pre>
          </div>
        )}
        {log.outputData !== null && log.outputData !== undefined && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Utdata:</p>
            <pre className="text-xs text-gray-700 bg-white p-2 rounded border overflow-x-auto">
              {JSON.stringify(log.outputData as object, null, 2)}
            </pre>
          </div>
        )}
        <div className="flex gap-4 text-xs text-gray-400">
          <span>Jobb-ID: {log.jobId || "-"}</span>
          <span>Varaktighet: {formatDuration(log.durationMs)}</span>
        </div>
      </div>
    );
  };

  const handleExport = () => {
    if (!data?.length) return;
    downloadCsv(
      data,
      [
        { header: "Jobbtyp", accessor: (r: JobLog) => r.jobType },
        { header: "Jobb-ID", accessor: (r: JobLog) => r.jobId || "" },
        { header: "Status", accessor: (r: JobLog) => r.status },
        { header: "Felmeddelande", accessor: (r: JobLog) => r.errorMessage || "" },
        { header: "Tid (ms)", accessor: (r: JobLog) => r.durationMs ?? "" },
        { header: "Tidpunkt", accessor: (r: JobLog) => new Date(r.createdAt).toLocaleString("sv-SE") },
      ],
      `jobblogg-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Visar körningslogg för e-postjobb, cron-jobb och bakgrundsuppgifter.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw size={16} className="mr-1" />
            Uppdatera
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!data?.length}
          >
            <Download size={16} className="mr-1" />
            Exportera
          </Button>
        </div>
      </div>

      <DataTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onPageChange={setPage}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageSizeChange={setPageSize}
        serverSortColumnId={sortColumnId}
        serverSortDirection={sortDirection}
        onSort={handleSort}
        onResetFilters={resetFilters}
        expandedContent={expandedContent}
        emptyMessage="Inga jobbloggar hittades"
        rowKey={(row) => row.id}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Settings Tab
// ═══════════════════════════════════════════════════════════════════

const EMAIL_SETTINGS = [
  {
    key: "email_deadline_reminder_days",
    label: "Deadline-påminnelser",
    description:
      "Antal dagar före deadline för årsredovisning som påminnelse-e-post skickas. Ange flera värden separerade med komma.",
    defaultValue: [90, 30, 7, 1],
    placeholder: "90, 30, 7, 1",
    hint: "Exempel: 90, 30, 7, 1 skickar påminnelser 90, 30, 7 och 1 dag(ar) före deadline.",
  },
  {
    key: "email_draft_reminder_days",
    label: "Utkast-påminnelse",
    description:
      "Antal dagar av inaktivitet innan påminnelse skickas för pågående utkast.",
    defaultValue: 14,
    placeholder: "14",
    hint: "Användare som inte uppdaterat sitt utkast på detta antal dagar får en påminnelse.",
  },
];

function SettingsTab() {
  const queryClient = useQueryClient();
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: settings, isLoading } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => adminApi.getSettings(),
    select: (res) => (res.success ? res.data : []),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      adminApi.updateSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
      toast.success("Inställningen sparades");
    },
    onError: () => {
      toast.error("Kunde inte spara inställningen");
    },
  });

  const getSettingValue = (key: string): string => {
    if (localValues[key] !== undefined) return localValues[key];
    const setting = settings?.find((s: SystemSetting) => s.key === key);
    if (setting?.value !== undefined) {
      if (Array.isArray(setting.value)) {
        return setting.value.join(", ");
      }
      return String(setting.value);
    }
    const meta = EMAIL_SETTINGS.find((s) => s.key === key);
    if (meta) {
      if (Array.isArray(meta.defaultValue)) {
        return meta.defaultValue.join(", ");
      }
      return String(meta.defaultValue);
    }
    return "";
  };

  const handleChange = (key: string, value: string) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSave = (key: string) => {
    const meta = EMAIL_SETTINGS.find((s) => s.key === key);
    const rawValue = localValues[key] ?? getSettingValue(key);

    let parsedValue: unknown;
    if (meta?.key === "email_deadline_reminder_days") {
      // Parse comma-separated list of numbers
      const numbers = rawValue
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n > 0);
      if (numbers.length === 0) {
        setErrors((prev) => ({ ...prev, [key]: "Ange minst ett giltigt tal" }));
        return;
      }
      parsedValue = numbers;
    } else {
      // Parse single number
      const num = parseInt(rawValue, 10);
      if (isNaN(num) || num < 1) {
        setErrors((prev) => ({ ...prev, [key]: "Ange ett giltigt tal större än 0" }));
        return;
      }
      parsedValue = num;
    }

    updateMutation.mutate({ key, value: parsedValue });
    setLocalValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Konfigurera intervall och tröskelvärden för automatiska e-postutskick och påminnelser.
      </p>

      <div className="space-y-6">
        {EMAIL_SETTINGS.map((setting) => {
          const hasLocalChange = localValues[setting.key] !== undefined;
          const error = errors[setting.key];

          return (
            <div
              key={setting.key}
              className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  {setting.label}
                </label>
                <p className="text-sm text-gray-500 mt-0.5">{setting.description}</p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Input
                    value={getSettingValue(setting.key)}
                    onChange={(e) => handleChange(setting.key, e.target.value)}
                    placeholder={setting.placeholder}
                    className={cn(
                      error && "border-red-500 focus:ring-red-500"
                    )}
                  />
                  {error && (
                    <p className="text-xs text-red-600 mt-1">{error}</p>
                  )}
                  {setting.hint && !error && (
                    <p className="text-xs text-gray-400 mt-1">{setting.hint}</p>
                  )}
                </div>
                <Button
                  onClick={() => handleSave(setting.key)}
                  disabled={!hasLocalChange || updateMutation.isPending}
                  size="sm"
                >
                  {updateMutation.isPending ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <Save size={16} className="mr-1" />
                  )}
                  Spara
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900">Om cron-jobb</h4>
        <p className="text-sm text-blue-700 mt-1">
          Cron-jobben körs automatiskt enligt schemat som är konfigurerat på servern.
          De anropar API:t med en hemlig nyckel för att trigga påminnelser och
          prenumerationskontroller. Se jobbloggen ovan för att övervaka körningarna.
        </p>
        <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
          <li><code className="bg-blue-100 px-1 rounded">/cron/check-deadlines</code> — Kollar deadline-påminnelser</li>
          <li><code className="bg-blue-100 px-1 rounded">/cron/check-drafts</code> — Kollar utkast-påminnelser</li>
          <li><code className="bg-blue-100 px-1 rounded">/cron/check-subscriptions</code> — Kollar prenumerationer</li>
        </ul>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Info Tab - Email Templates & Cron Jobs Overview
// ═══════════════════════════════════════════════════════════════════

interface EmailTemplateInfo {
  type: string;
  name: string;
  description: string;
  trigger: string;
  category: "auth" | "subscription" | "sharebook" | "annual_report" | "notification";
}

const EMAIL_TEMPLATES: EmailTemplateInfo[] = [
  // Auth & User
  {
    type: "welcome",
    name: "Välkomstmail",
    description: "Skickas när en ny användare registrerar sig",
    trigger: "Vid registrering",
    category: "auth",
  },
  {
    type: "verification",
    name: "E-postverifiering",
    description: "Verifieringslänk för e-postadress",
    trigger: "Vid registrering / begäran",
    category: "auth",
  },
  {
    type: "password_reset",
    name: "Lösenordsåterställning",
    description: "Länk för att återställa lösenord",
    trigger: "Vid begäran om återställning",
    category: "auth",
  },
  {
    type: "two_factor_enabled",
    name: "2FA aktiverad",
    description: "Bekräftelse att tvåfaktorsautentisering aktiverats",
    trigger: "När användare aktiverar 2FA",
    category: "auth",
  },
  {
    type: "two_factor_disabled",
    name: "2FA inaktiverad",
    description: "Bekräftelse att tvåfaktorsautentisering inaktiverats",
    trigger: "När användare inaktiverar 2FA",
    category: "auth",
  },
  {
    type: "member_invitation",
    name: "Företagsinbjudan",
    description: "Inbjudan att gå med i ett företag",
    trigger: "När ägare bjuder in medlem",
    category: "auth",
  },

  // Subscription & Payment
  {
    type: "subscription_renewal_reminder",
    name: "Förnyelspåminnelse",
    description: "Påminnelse om kommande prenumerationsförnyelse",
    trigger: "Cron: 7 och 1 dag före förnyelse",
    category: "subscription",
  },
  {
    type: "payment_receipt",
    name: "Betalningskvitto",
    description: "Kvitto efter genomförd betalning",
    trigger: "Efter lyckad betalning",
    category: "subscription",
  },
  {
    type: "payment_failed",
    name: "Misslyckad betalning",
    description: "Meddelande om misslyckad betalning",
    trigger: "När betalning misslyckas",
    category: "subscription",
  },
  {
    type: "free_year_ending",
    name: "Gratisår upphör",
    description: "Påminnelse att gratisåret snart upphör",
    trigger: "Cron: 30, 7 och 1 dag före",
    category: "subscription",
  },
  {
    type: "subscription_cancelled",
    name: "Prenumeration avslutad",
    description: "Bekräftelse att prenumeration avslutats",
    trigger: "När prenumeration avslutas",
    category: "subscription",
  },

  // Share Book
  {
    type: "share_book_created",
    name: "Aktiebok skapad",
    description: "Bekräftelse att aktiebok skapats",
    trigger: "När aktiebok skapas",
    category: "sharebook",
  },
  {
    type: "shareholder_added",
    name: "Aktieägare tillagd",
    description: "Notifikation om ny aktieägare",
    trigger: "När aktieägare läggs till",
    category: "sharebook",
  },
  {
    type: "shareholders_imported",
    name: "Aktieägare importerade",
    description: "Sammanfattning efter massimport",
    trigger: "Efter import av aktieägare",
    category: "sharebook",
  },
  {
    type: "transaction_registered",
    name: "Transaktion registrerad",
    description: "Notifikation om aktietransaktion",
    trigger: "Vid köp/försäljning/gåva",
    category: "sharebook",
  },
  {
    type: "capital_change",
    name: "Kapitalförändring",
    description: "Notifikation om aktiekapitalförändring",
    trigger: "Vid nyemission/split/etc",
    category: "sharebook",
  },
  {
    type: "pledge_changed",
    name: "Pantsättning ändrad",
    description: "Notifikation om pantsättning av aktier",
    trigger: "Vid pantsättning/avpantsättning",
    category: "sharebook",
  },

  // Annual Report
  {
    type: "annual_report_deadline",
    name: "Deadline-påminnelse",
    description: "Påminnelse om kommande deadline för årsredovisning",
    trigger: "Cron: Konfigurerbart (default 90, 30, 7, 1 dagar)",
    category: "annual_report",
  },
  {
    type: "annual_report_draft_reminder",
    name: "Utkast-påminnelse",
    description: "Påminnelse om pågående utkast som inte uppdaterats",
    trigger: "Cron: Efter X dagars inaktivitet (default 14)",
    category: "annual_report",
  },
];

interface CronJobInfo {
  endpoint: string;
  name: string;
  description: string;
  schedule: string;
  emails: string[];
}

const CRON_JOBS: CronJobInfo[] = [
  {
    endpoint: "/cron/check-deadlines",
    name: "Deadline-kontroll",
    description: "Kollar alla årsredovisningar och skickar påminnelser baserat på konfigurerade intervall",
    schedule: "Dagligen 08:00",
    emails: ["annual_report_deadline"],
  },
  {
    endpoint: "/cron/check-draft-reminders",
    name: "Utkast-påminnelser",
    description: "Hittar pågående utkast som inte uppdaterats på X dagar och skickar påminnelse",
    schedule: "Dagligen 09:00",
    emails: ["annual_report_draft_reminder"],
  },
  {
    endpoint: "/cron/check-subscriptions",
    name: "Prenumerationskontroll",
    description: "Kontrollerar prenumerationer för förnyelse, utgång och gratisår",
    schedule: "Dagligen 10:00",
    emails: ["subscription_renewal_reminder", "free_year_ending"],
  },
];

const CATEGORY_INFO: Record<EmailTemplateInfo["category"], { label: string; icon: React.ElementType; color: string }> = {
  auth: { label: "Autentisering & Användare", icon: Users, color: "bg-blue-100 text-blue-700" },
  subscription: { label: "Prenumeration & Betalning", icon: CreditCard, color: "bg-green-100 text-green-700" },
  sharebook: { label: "Aktiebok", icon: FileText, color: "bg-purple-100 text-purple-700" },
  annual_report: { label: "Årsredovisning", icon: CalendarClock, color: "bg-orange-100 text-orange-700" },
  notification: { label: "Notifikationer", icon: Bell, color: "bg-yellow-100 text-yellow-700" },
};

function InfoTab() {
  const categories = ["auth", "subscription", "sharebook", "annual_report"] as const;

  return (
    <div className="space-y-8">
      <p className="text-sm text-gray-500">
        Översikt över alla e-postmallar och schemalagda jobb i systemet.
      </p>

      {/* Email Templates by Category */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Send size={20} />
          E-postmallar
        </h2>

        {categories.map((category) => {
          const info = CATEGORY_INFO[category];
          const templates = EMAIL_TEMPLATES.filter((t) => t.category === category);
          const Icon = info.icon;

          return (
            <div key={category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className={cn("px-4 py-3 flex items-center gap-2", info.color)}>
                <Icon size={18} />
                <h3 className="font-medium">{info.label}</h3>
                <span className="text-xs opacity-75">({templates.length} mallar)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Typ</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Namn</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600 hidden md:table-cell">Beskrivning</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600 hidden lg:table-cell">Trigger</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {templates.map((template) => (
                      <tr key={template.type} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{template.type}</code>
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900">{template.name}</td>
                        <td className="px-4 py-2 text-gray-500 hidden md:table-cell">{template.description}</td>
                        <td className="px-4 py-2 text-gray-500 hidden lg:table-cell">{template.trigger}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cron Jobs */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock size={20} />
          Schemalagda jobb (Cron)
        </h2>

        <div className="grid gap-4">
          {CRON_JOBS.map((job) => (
            <div key={job.endpoint} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="font-medium text-gray-900">{job.name}</h3>
                  <p className="text-sm text-gray-500">{job.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">{job.endpoint}</code>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{job.schedule}</span>
                  </div>
                </div>
              </div>
              {job.emails.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-1">Triggar e-postmallar:</p>
                  <div className="flex flex-wrap gap-1">
                    {job.emails.map((email) => (
                      <code key={email} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                        {email}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Technical Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-2">Teknisk information</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• E-post köas via <strong>BullMQ</strong> och processas av <code className="bg-gray-200 px-1 rounded">email.worker.ts</code></li>
          <li>• Cron-jobb triggas via HTTP-anrop med hemlig nyckel (<code className="bg-gray-200 px-1 rounded">CRON_SECRET</code>)</li>
          <li>• Alla jobb loggas i <code className="bg-gray-200 px-1 rounded">job_execution_log</code>-tabellen (se Jobblogg-fliken)</li>
          <li>• E-postmallar renderas med <strong>React Email</strong> och skickas via SMTP (Nodemailer)</li>
        </ul>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export function AdminEmailCron() {
  const [activeTab, setActiveTab] = useState<"logs" | "settings" | "info">("logs");

  return (
    <>
      <PageMeta {...ADMIN_PAGE_META.settings} />

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">E-post & Cron</h1>
          <p className="text-gray-500 mt-1">
            Övervaka e-postutskick, cron-jobb och konfigurera påminnelser.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors",
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                )}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === "logs" && <JobLogsTab />}
          {activeTab === "settings" && <SettingsTab />}
          {activeTab === "info" && <InfoTab />}
        </div>
      </div>
    </>
  );
}
